import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  Link,
} from "react-router-dom";
import { LogOut } from "lucide-react";

import ScrollToTop from "./ScrollToTop";
import "./styles/theme.css"; // design system (tokens + componentes)
import { ToastHost } from "./components/Toast";
import BottomNav from "./components/BottomNav";
import Sidebar from "./components/Sidebar";
import useIsMobileDevice from "./hooks/useIsMobileDevice";
import { isLoggedIn, getUser, logout } from "./services/auth";

/* ===========================
   Lazy routes (code-splitting)
   =========================== */
// Cadastro público fechado: /cadastro mostra a lista de interesse. Para liberar o
// cadastro, voltar a rota para pages/Cadastro e ligar CADASTRO_ABERTO no backend.
const Interesse                        = lazy(() => import("./pages/Interesse/Interesse"));
const RecuperarSenha                   = lazy(() => import("./pages/RecuperarSenha/RecuperarSenha"));
const Login                            = lazy(() => import("./pages/Login/Login"));
const Dashboard                        = lazy(() => import("./pages/Dashboard/Dashboard"));
const RedefinirSenha                   = lazy(() => import("./pages/RedefinirSenha/RedefinirSenha"));
const ValidarCodigo                    = lazy(() => import("./pages/ValidarCodigo/ValidarCodigo"));
const CadastrarProduto                 = lazy(() => import("./pages/CadastrarProduto/CadastrarProduto"));
const ListaProdutos                    = lazy(() => import("./pages/ListaProdutos/ListaProdutos"));
const ImportarProdutos                 = lazy(() => import("./pages/ImportarProdutos/ImportarProdutos"));
const ContarEstoque                    = lazy(() => import("./pages/ContarEstoque/ContarEstoqueSwitch"));
const CadastrarProdutoParaContagem     = lazy(() => import("./pages/CadastrarProdutoParaContagem/CadastrarProdutoParaContagem"));
const ListaProdutosParaContagem        = lazy(() => import("./pages/ListaProdutosParaContagem/ListaProdutosParaContagem"));
const MinhasContagens                  = lazy(() => import("./pages/MinhasContagens/MinhasContagens"));
const LandingPage                      = lazy(() => import("./pages/LandingPage/LandingPage"));
const NotFound                         = lazy(() => import("./pages/NotFound/NotFound"));
const VideoTutorial                    = lazy(() => import("./pages/VideoTutorial/VideoTutorial"));
const ListaDeCompra                    = lazy(() => import("./pages/ListaDeCompra/ListaDeCompra"));
const ReceberMercadorias               = lazy(() => import("./pages/ReceberMercadorias/ReceberMercadorias"));
const Logout                           = lazy(() => import("./pages/Logout/Logout"));
const CotacaoPublica                   = lazy(() => import("./pages/CotacaoPublica/CotacaoPublica"));
const ConferirCotacoes                 = lazy(() => import("./pages/ConferirCotacoes/ConferirCotacoes"));
const CompararCotacoes                 = lazy(() => import("./pages/ConferirCotacoes/CompararCotacoes"));
const CotacaoManual                    = lazy(() => import("./pages/ConferirCotacoes/CotacaoManual"));
const MensalInicio                     = lazy(() => import("./pages/ContagemMensal/MensalInicio"));
const ContagemMensal                   = lazy(() => import("./pages/ContagemMensal/ContagemMensal"));
const HistoricoMensal                  = lazy(() => import("./pages/ContagemMensal/HistoricoMensal"));

/* ===========================
   Header global (dinâmico)
   Computador com login: o menu fica na lateral (Sidebar) e este header não aparece.
   =========================== */

/* Usuário logado, reagindo a login/logout (mesma aba) e a outras abas */
function useAuthUser() {
  const [user, setUserState] = useState(getUser());

  // Mantém sessão ao recarregar e sincroniza alterações (outras abas / logout / login)
  useEffect(() => {
    const handleAuthChange = () => setUserState(getUser());

    window.addEventListener("auth-changed", handleAuthChange);
    const handleStorage = (e) => {
      // escuta a chave correta
      if (!e || e.key === "ec_user") handleAuthChange();
    };
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("auth-changed", handleAuthChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return user;
}

/* Página pública do fornecedor (/cotacao/:token): sem menu, conta ou links do sistema,
   mesmo que quem abra esteja logado neste navegador */
function usePaginaPublica() {
  const { pathname } = useLocation();
  return pathname.startsWith("/cotacao/");
}

function GlobalHeader({ user }) {
  const navigate = useNavigate();
  const paginaPublica = usePaginaPublica();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const nomeExibicao = user?.nome || user?.name || "Usuário";
  const inicial = nomeExibicao.trim().charAt(0).toUpperCase() || "U";

  return (
    <header className="app-header" role="banner">
      <div className="app-header-inner">
        {/* Skip link para acessibilidade */}
        <a className="sr-only" href="#main-content">Pular para conteúdo principal</a>

        {paginaPublica ? (
          <span className="brand">
            <img src="/logo.png" alt="" className="brand-logo" />
            <span className="brand-name">Estoque<span>Contado</span></span>
          </span>
        ) : (
          <Link to={user ? "/dashboard" : "/"} className="brand" aria-label="Estoque Contado - Início">
            <img src="/logo.png" alt="" className="brand-logo" />
            <span className="brand-name">Estoque<span>Contado</span></span>
          </Link>
        )}

        {!paginaPublica && <div className="header-actions">
          {user ? (
            <>
              <span className="user-chip" title={user.email}>
                <span className="user-avatar" aria-hidden="true">{inicial}</span>
                <span className="user-name">{nomeExibicao}</span>
              </span>
              <button type="button" onClick={handleLogout} className="btn btn-ghost btn-sm" aria-label="Sair">
                <LogOut aria-hidden="true" />
                <span>Sair</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-ghost btn-sm">Entrar</Link>
              <Link to="/cadastro" className="btn btn-primary btn-sm">Quero ser avisado</Link>
            </>
          )}
        </div>}
      </div>
    </header>
  );
}

/* ===========================
   Foco no <main> a cada troca de rota
   =========================== */
function FocusMainOnRouteChange({ children }) {
  const location = useLocation();
  const mainRef = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (mainRef.current) mainRef.current.focus({ preventScroll: true });
    });
  }, [location]);

  return React.cloneElement(children, { mainRef });
}

/* ===========================
   Fallback para lazy loading
   =========================== */
function AppFallback() {
  return (
    <div className="loading-block" role="status" aria-live="polite">
      <div className="spinner spinner-lg" aria-hidden="true" />
      Carregando…
    </div>
  );
}

/* ===========================
   Proteção de rotas
   =========================== */
function PrivateRoute({ children }) {
  return isLoggedIn() ? children : <Navigate to="/login" replace />;
}

/* Redireciona automaticamente se já logado (para / e /login),
   e reage quando a auth mudar (login/logout em tempo real) */
function AutoRedirectToDashboard() {
  const navigate = useNavigate();

  useEffect(() => {
    const go = () => {
      if (isLoggedIn()) navigate("/dashboard", { replace: true });
    };

    // checa já no mount
    go();

    // reage a login/logout (mesma aba) e a mudanças entre abas
    window.addEventListener("auth-changed", go);
    window.addEventListener("storage", go);

    return () => {
      window.removeEventListener("auth-changed", go);
      window.removeEventListener("storage", go);
    };
  }, [navigate]);

  return null;
}

/* ===========================
   App
   =========================== */
export default function App() {
  return (
    <Router>
      <ScrollToTop />
      <AppShell />
    </Router>
  );
}

function AppShell() {
  const user = useAuthUser();
  const isMobile = useIsMobileDevice();
  const paginaPublica = usePaginaPublica();
  const { pathname } = useLocation();
  // Sem a barra na Home (os cards já dão acesso a tudo) e na contagem (tela cheia,
  // com o próprio "←" e botões de finalizar no rodapé); nas demais páginas ela fica.
  // Cotação manual também é tela cheia: a barra cobriria a calculadora e o "Salvar".
  const semBarra =
    ["/dashboard", "/contar-estoque", "/contagem-mensal/contar"].includes(pathname) ||
    /^\/cotacoes\/\d+\/manual(\/\d+)?$/.test(pathname);
  const showBottomNav = !!user && isMobile && !paginaPublica && !semBarra;
  // computador com login: menu lateral no lugar do header (páginas públicas ficam como estão)
  const comSidebar = !!user && !isMobile && !paginaPublica;

  return (
    <div className={`app-shell ${showBottomNav ? "has-bottom-nav" : ""} ${comSidebar ? "has-sidebar" : ""}`}>
      {comSidebar ? <Sidebar user={user} /> : <GlobalHeader user={user} />}

      <FocusMainOnRouteChange>
        <MainRoutes />
      </FocusMainOnRouteChange>

      <footer className="app-footer" role="contentinfo">
        <div className="app-footer-inner">
          <span>© {new Date().getFullYear()} Estoque Contado</span>
          <span>Controle de estoque simples e confiável</span>
        </div>
      </footer>

      {showBottomNav && <BottomNav />}

      <ToastHost />
    </div>
  );
}

/* ===========================
   Main + Rotas
   =========================== */
function MainRoutes({ mainRef }) {
  return (
    <main
      className="app-main"
      id="main-content"
      role="main"
      tabIndex={-1}
      ref={mainRef}
    >
      <Suspense fallback={<AppFallback />}>
        <Routes>
          {/* Landing Page — se logado, vai pro dashboard */}
          <Route
            path="/"
            element={
              <>
                <AutoRedirectToDashboard />
                <LandingPage />
              </>
            }
          />

          {/* Autenticação — se logado, redireciona */}
          <Route
            path="/login"
            element={
              <>
                <AutoRedirectToDashboard />
                <Login />
              </>
            }
          />
          <Route path="/cadastro" element={<Interesse />} />
          <Route path="/recuperar-senha" element={<RecuperarSenha />} />
          <Route path="/validar-codigo" element={<ValidarCodigo />} />
          <Route path="/redefinir-senha/:token" element={<RedefinirSenha />} />
          <Route path="/video-tutorial" element={<VideoTutorial />} />

          {/* Dashboard (privado) */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />

          {/* Produtos (privado) */}
          <Route
            path="/cadastrar-produto"
            element={
              <PrivateRoute>
                <CadastrarProduto />
              </PrivateRoute>
            }
          />
          <Route
            path="/editar-produto/:id"
            element={
              <PrivateRoute>
                <CadastrarProduto />
              </PrivateRoute>
            }
          />
          <Route
            path="/lista-produtos"
            element={
              <PrivateRoute>
                <ListaProdutos />
              </PrivateRoute>
            }
          />
          <Route
            path="/importar-produtos"
            element={
              <PrivateRoute>
                <ImportarProdutos />
              </PrivateRoute>
            }
          />

          {/* Contagem de estoque (privado) */}
          <Route
            path="/contar-estoque"
            element={
              <PrivateRoute>
                <ContarEstoque />
              </PrivateRoute>
            }
          />
          <Route
            path="/cadastrar-produto-para-contagem"
            element={
              <PrivateRoute>
                <CadastrarProdutoParaContagem />
              </PrivateRoute>
            }
          />
          <Route
            path="/editar-produto-para-contagem/:id"
            element={
              <PrivateRoute>
                <CadastrarProdutoParaContagem />
              </PrivateRoute>
            }
          />
          <Route
            path="/lista-produtos-para-contagem"
            element={
              <PrivateRoute>
                <ListaProdutosParaContagem />
              </PrivateRoute>
            }
          />
          <Route
            path="/minhas-contagens"
            element={
              <PrivateRoute>
                <MinhasContagens />
              </PrivateRoute>
            }
          />

          {/* Lista de Compra (privado) */}
          <Route
            path="/lista-de-compra/:id"
            element={
              <PrivateRoute>
                <ListaDeCompra />
              </PrivateRoute>
            }
          />

          {/* Receber Mercadorias (privado) */}
          <Route
            path="/receber-mercadorias"
            element={
              <PrivateRoute>
                <ReceberMercadorias />
              </PrivateRoute>
            }
          />

          {/* Cotações (privado) */}
          <Route
            path="/cotacoes"
            element={
              <PrivateRoute>
                <ConferirCotacoes />
              </PrivateRoute>
            }
          />
          <Route
            path="/cotacoes/:listaId"
            element={
              <PrivateRoute>
                <CompararCotacoes />
              </PrivateRoute>
            }
          />
          <Route
            path="/cotacoes/:listaId/manual"
            element={
              <PrivateRoute>
                <CotacaoManual />
              </PrivateRoute>
            }
          />
          <Route
            path="/cotacoes/:listaId/manual/:cotacaoId"
            element={
              <PrivateRoute>
                <CotacaoManual />
              </PrivateRoute>
            }
          />

          {/* Cotação do fornecedor (pública, sem login) */}
          {/* Contagem mensal — valor do estoque em R$ (privado) */}
          <Route
            path="/contagem-mensal"
            element={
              <PrivateRoute>
                <MensalInicio />
              </PrivateRoute>
            }
          />
          <Route
            path="/contagem-mensal/contar"
            element={
              <PrivateRoute>
                <ContagemMensal />
              </PrivateRoute>
            }
          />
          <Route
            path="/contagem-mensal/historico"
            element={
              <PrivateRoute>
                <HistoricoMensal />
              </PrivateRoute>
            }
          />
          <Route
            path="/contagem-mensal/historico/:id"
            element={
              <PrivateRoute>
                <HistoricoMensal />
              </PrivateRoute>
            }
          />

          <Route path="/cotacao/:token" element={<CotacaoPublica />} />

          {/* Logout */}
          <Route path="/logout" element={<Logout />} />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </main>
  );
}
